<template>
    <div class="journey">
        <div class="journey__expand-header" @click="selectJourney">
            <journey-header :start-point="journey.startPoint" :end-point="journey.endPoint" :distance="distance" :is-current-journey="journey.id === 'current'" :duration="duration" :is-active="isJourneySelected"></journey-header>
        </div>
        <div class="journey__content" v-show="isJourneySelected">
            <journey-details
                :start-point="journey.startPoint"
                :end-point="journey.endPoint"
                :distance="distance"
                :duration="duration"
                :journey-id="journey.id"
                :vehicle-id="journey.vehicleId"
                :hardware-id="journey.hardwareId"
                :device-details="journey.deviceDetails"
                :has-raw-data="journey.telemetryUnsnapped != null && journey.telemetryUnsnapped.route != null"
                :has-speeding-data="journey.telemetryExtended != null && journey.telemetryExtended.journeySpeedPoints != null && journey.telemetryExtended.journeySpeedPoints.length > 0"
                :current-length-meters="journey.telemetryExtended ? journey.telemetryExtended.lengthMeters : 0"
                :is-current-journey="journey.id === 'current'"
            ></journey-details>

            <driver-behaviour v-if="journey.telemetryExtended != null"
                :events="journey.events"
                :driver="journey.driverName"
                :max-speed="journey.telemetryExtended.maxSpeed"
                :telemetry-extended="journey.telemetryExtended"
            ></driver-behaviour>

            <journey-events :events="journey.events"></journey-events>
        </div>
    </div>
</template>

<script>
import Journey from '@/models/Journey'
import { mapActions, mapGetters } from 'vuex'
import JourneyHeader from '@/views/Tracking/components/Aside/JourneyDetails/JourneyItem/JourneyHeader'
import JourneyDetails from '@/views/Tracking/components/Aside/JourneyDetails/JourneyItem/JourneyDetails'
import JourneyEvents from '@/views/Tracking/components/Aside/JourneyDetails/JourneyItem/JourneyEvents'
import moment from 'moment'
import { speedOrDistanceUnitManager } from '@/helpers/Utils'
import { JourneyApi, TelemetryApi } from '@/api'
import JourneyTelemetryExtendedTransformer from '@/transformers/JourneyTelemetryExtendedTransformer'
import toast from '@/plugins/izitoast'
import { TimelineTransformer } from '@/transformers/TimelineTransformer'
import DriverBehaviour from '@/views/Tracking/components/Aside/JourneyDetails/JourneyItem/DriverBehaviour'
import JourneyTelemetryExtendedV2Transformer from '@/transformers/JourneyTelemetryExtendedV2Transformer';

const {DeviceApi} = require("@/api");

const journeyApi = new JourneyApi()
const telemetryApi = new TelemetryApi()
const journeyTelemetryExtendedTransformer = new JourneyTelemetryExtendedTransformer()
const journeyTelemetryExtendedV2Transformer = new JourneyTelemetryExtendedV2Transformer()
const timelineTransformer = new TimelineTransformer()

export default {
    name: 'Journey',
    components: { DriverBehaviour, JourneyEvents, JourneyDetails, JourneyHeader },
    props: {
        journey: {
            type: Journey,
            required: true
        },
        index: {
            type: Number,
            required: true
        }
    },
    watch: {
        journey: {
            async handler () {
                console.debug('Journey::watch', 0 === this.index, this.getIsJourneyTabActive, this.getOpenAutoJourney)
                if (0 === this.index && this.getIsJourneyTabActive && this.getOpenAutoJourney) {
                    await this.selectJourney()
                    if (this.journey.telemetryExtended) {
                        await this.setOpenAutoJourney(false)
                    }
                }
            },
            deep: true,
        }
    },
    async mounted () {
        console.debug('Journey', this.journey)

        console.debug('Journey::mounted', 0 === this.index, this.getIsJourneyTabActive, this.getOpenAutoJourney)
        if (0 === this.index && this.getIsJourneyTabActive && this.getOpenAutoJourney) {
            await this.selectJourney()
            if (this.journey.telemetryExtended) {
                await this.setOpenAutoJourney(false)
            }
        }
    },
    computed: {
        ...mapGetters({
            selectedJourneys: 'trackingModule/getSelectedJourneys',
            getIsJourneyTabActive: 'trackingModule/getIsJourneyTabActive',
            getOpenAutoJourney: 'trackingModule/getOpenAutoJourney',
        }),
        distance() {
            return speedOrDistanceUnitManager((this.journey.lengthMeters / 1000), 'distance')
        },
        duration: function () {
            if (!this.journey.endPoint.utc) return 'Active'

            const start = moment(this.journey.startPoint.utc)
            const end = moment(this.journey.endPoint.utc)
            return moment.utc(moment(end, 'HH:mm:ss').diff(moment(start, 'HH:mm:ss'))).format('HH:mm:ss')
        },
        isJourneySelected: function () {
            const result = this.selectedJourneys.find((tmpJourney) => {
                return tmpJourney.id === this.journey.id
            })

            return null != result
        }
    },
    methods: {
        ...mapActions({
            addSelectedJourney: 'trackingModule/addSelectedJourney',
            removeSelectedJourney: 'trackingModule/removeSelectedJourney',
            setLoading: 'trackingModule/setLoading',
            setOpenAutoJourney: 'trackingModule/setOpenAutoJourney',
            clearSelectedJourneys: 'trackingModule/clearSelectedJourneys'
        }),
        selectJourney: async function () {
            if (this.isJourneySelected) {
                this.removeSelectedJourney(this.journey)

                return
            }

            this.setLoading(true)
            // this.clearSelectedJourneys()

            let result
            if (['current', 'unprocessed'].includes(this.journey.type)) {
                result = await telemetryApi.getTelemetry(this.journey.hardwareId, this.journey.startPoint.utc, this.journey.endPoint.utc, false)
            } else {
                result = await journeyApi.getJourney(this.journey.id, false)
            }

            if (!result.isSuccess) {
                toast.error('Cannot find requested journey.')
                this.setLoading(false)
                return
            }

            this.journey.telemetryExtended = journeyTelemetryExtendedV2Transformer.transform(result.payload)

            // const resultTimeline = await  journeyApi.getJourneyTimeline(this.journey.startPoint.utc, this.journey.endPoint.utc, this.journey.vehicleId)

            if (this.journey.vehicleId) {
                const timelineResponse = await journeyApi.getJourneyTimeline(this.journey.startPoint.utc, this.journey.endPoint.utc, this.journey.vehicleId)

                if (!timelineResponse.isSuccess) {
                    toast.error('Timeline not found')
                    return
                }
                if (process.env.VUE_APP_DEBUG) {
                    console.debug('payload', timelineResponse.payload)
                }
                this.journey.timeline = timelineTransformer.transform(timelineResponse.payload)
            };

            const telemetryUnsnappedResponse = await telemetryApi.getTelemetry(this.journey.hardwareId, this.journey.startPoint.utc, this.journey.endPoint.utc, true)
            if (process.env.VUE_APP_DEBUG) {
                console.debug('telemetryResponse', telemetryUnsnappedResponse)
            }
            this.journey.telemetryUnsnapped = journeyTelemetryExtendedV2Transformer.transform(telemetryUnsnappedResponse.payload)

            if (process.env.VUE_APP_DEBUG) {
                console.debug('Journey - full', this.journey)
            }

            // this.setCurrentVehicle(vehicle)
            this.addSelectedJourney(this.journey)
            this.setLoading(false)
        }

        // request for media and events
    }
}
</script>

<style scoped lang="scss">
.journey {
    padding: 0 15px;
}

.live {
    display: flex;
    align-items: center;
    border: 1px solid $danger;
    border-radius: 50px;
    padding: 0 11px 0 15px;
    position: relative;
    font-size: 11px;

    &::before {
        content: '';
        display: inline-block;
        position: absolute;
        left: 6px;
        width: 5px;
        height: 5px;
        background: $danger;
        border-radius: 50%;
        animation: pulse 1s infinite alternate-reverse;
    }
}
</style>

<style lang="scss">
h2 {
    margin: 0;
    padding: 0;
    font-size: 16px;

    &::after {
        content: '';
        width: 30px;
        height: 3px;
        background: $primary;
        display: block;
        margin-top: 6px;
    }
}

.v-expansion-panel__header {
    padding: 6px 4px;
}

.v-expansion-panel__body {
    padding: 6px 4px;
}
</style>
